(() => {
  'use strict';
  if (customElements.get('ks-shipping-summary')) return;

  // Read only the final quote, never the crossed-out price. The app may use
  // English punctuation even on a translated storefront, so accept both.
  function quoteInCents(text, currency) {
    const value = String(text || '').trim();
    if (!value || !/\d/.test(value) || /[-−]\s*\d/.test(value)) return null;
    const codes = value.match(/\b[A-Z]{3}\b/g) || [];
    if (codes.some((code) => code !== currency)) return null;
    const expected = new Intl.NumberFormat('en', { style: 'currency', currency })
      .formatToParts(1).find((part) => part.type === 'currency')?.value;
    if (/[€£¥₹₩]/.test(value) && !value.includes(expected)) return null;
    const digits = value.replace(/[^\d.,]/g, '');
    if (!digits) return null;
    const decimal = digits.match(/[.,](\d{1,2})$/);
    const whole = decimal ? digits.slice(0, decimal.index) : digits;
    if (!/^\d{1,3}(?:[.,]\d{3})*$|^\d+$/.test(whole)) return null;
    const cents = Number(whole.replace(/[.,]/g, '')) * 100
      + (decimal ? Number(decimal[1].padEnd(2, '0')) : 0);
    return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
  }

  class ShippingSummary extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.drawer = this.closest('cart-drawer');
      if (!this.drawer) return;
      this.baseTotal = Number(this.dataset.cartTotal);
      this.currency = this.dataset.currency || 'USD';
      try {
        this.formatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency', currency: this.currency
        });
      } catch (_) { return; }
      this.price = this.querySelector('[data-protection-price]');
      this.total = this.drawer.querySelector('.cart-total .amount');
      this.label = this.drawer.querySelector('[data-ks-total-label]');
      if (!this.price || !this.total || !this.label || !Number.isSafeInteger(this.baseTotal)) return;
      this.originalTotal = this.total.textContent;
      this.originalLabel = this.label.textContent;
      this.projectedTotal = null;
      this.schedule = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.sync(), 180);
      };
      this.observer = new MutationObserver((records) => {
        // Our own price/label writes must not trigger a render loop.
        if (this.total.textContent !== this.originalTotal && this.total.textContent !== this.projectedTotal) {
          // Another cart renderer owns the new amount. Wait for a fresh Liquid
          // snapshot rather than adding a quote to an unverified base amount.
          this.externalTotalChange = true;
          this.originalTotal = this.total.textContent;
          this.schedule();
        }
        if (records.some((record) => !this.contains(record.target)
          && !this.total.contains(record.target) && !this.label.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.drawer, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'hidden', 'aria-busy']
      });
      this.onQuantityChange = (event) => {
        if (!event.target.matches('.qty')) return;
        this.restore();
        clearTimeout(this.timer);
        // A new server-rendered component replaces this snapshot after success.
        // If the update fails, keep the original cart amount until refreshed.
        this.quantityPending = true;
      };
      this.drawer.addEventListener('input', this.onQuantityChange);
      this.drawer.addEventListener('change', this.onQuantityChange);
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.observer = null;
      this.drawer?.removeEventListener('input', this.onQuantityChange);
      this.drawer?.removeEventListener('change', this.onQuantityChange);
    }

    restore() {
      this.hidden = true;
      if (this.total && this.total.textContent !== this.originalTotal) this.total.textContent = this.originalTotal;
      if (this.label && this.label.textContent !== this.originalLabel) this.label.textContent = this.originalLabel;
    }

    sync() {
      if (!this.isConnected) return;
      if (this.quantityPending || this.externalTotalChange || this.dataset.inCart === 'true' || this.drawer._updating) {
        this.restore();
        return;
      }
      const widget = Array.from(this.drawer.querySelectorAll('.xcotton_wrapper'))
        .find((node) => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
      const selected = widget?.querySelector('#xcotton-switch.checked');
      const quote = widget && Array.from(widget.querySelectorAll('.xcotton_price:not(.xcotton_price_tag)'))
        .find((node) => node.getClientRects().length);
      const cents = quote && selected ? quoteInCents(quote.textContent, this.currency) : null;
      if (cents === null || !Number.isSafeInteger(this.baseTotal + cents)) {
        this.restore();
        return;
      }
      const price = this.formatter.format(cents / 100);
      const total = this.formatter.format((this.baseTotal + cents) / 100);
      this.projectedTotal = total;
      if (this.price.textContent !== price) this.price.textContent = price;
      if (this.total.textContent !== total) this.total.textContent = total;
      if (this.label.textContent !== 'Estimated total') this.label.textContent = 'Estimated total';
      this.hidden = false;
    }
  }
  customElements.define('ks-shipping-summary', ShippingSummary);
})();
